// apps/vendor/src/pages/SupportDetail.tsx
//
// dbg-support-polish — vendor Support DETAIL page, 2-column admin-parity
// layout for BOTH ticket kinds:
//   /support/tickets/:id   — vendor-raised ticket (mine): conversation with
//                            Printway support + reply; sidebar: status/
//                            priority/category/escalation.
//   /support/assigned/:id  — pushed from admin: description + PRIVATE
//                            admin↔vendor thread + reply; sidebar: work-state
//                            select (Pending/InProgress/Done) + meta.

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageHeader, useAuth } from '@printway/ui';

import { useTranslation } from '../lib/i18n/index.js';
import {
  displayStatus,
  useAssignedDetail,
  useAssignedReply,
  useAssignedStatusUpdate,
  useCloseTicket,
  useRateTicket,
  useReplyTicket,
  useTicketDetail,
  type AssignedVendorStatus,
  type TicketDisplayStatus,
  type TicketRow,
} from '../features/support/api.js';

const STATUS_PILL: Record<TicketDisplayStatus, string> = {
  Open: 'bg-blue-100 text-blue-800',
  InProgress: 'bg-yellow-100 text-yellow-800',
  Resolved: 'bg-green-100 text-green-800',
  Closed: 'bg-pw-surface text-pw-text',
  Reopened: 'bg-purple-100 text-purple-800',
};
const ASSIGNED_STATUS_PILL: Record<AssignedVendorStatus, string> = {
  Pending: 'bg-amber-100 text-amber-800',
  InProgress: 'bg-blue-100 text-blue-800',
  Done: 'bg-green-100 text-green-800',
};
const ASSIGNED_STATUSES: AssignedVendorStatus[] = ['Pending', 'InProgress', 'Done'];

function fmtMs(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function BackLink(): JSX.Element {
  const { t } = useTranslation('support');
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate('/support')}
      className="inline-flex items-center gap-1 text-sm text-pw-text-muted hover:text-pw-text"
    >
      ← {t('detailV2.back')}
    </button>
  );
}

function SidebarRow({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="text-pw-text-muted">{label}</span>
      <span className="text-right text-pw-text">{children}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rating — vendor rates Printway support (feedback (b), dbg-20260707).
// Mirror of the merchant flow: form once resolved/closed, read-only after.
// ---------------------------------------------------------------------------

function RatingBlock({ ticket }: { ticket: TicketRow }): JSX.Element {
  const { t } = useTranslation('support');
  const rateMut = useRateTicket();
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');

  if (typeof ticket.rating === 'number') {
    return (
      <div
        className="space-y-2 rounded-xl border border-pw-border bg-pw-bg p-4"
        data-testid="vendor-ticket-rating-done"
      >
        <p className="text-sm font-medium text-pw-text">{t('rating.thanks')}</p>
        <div className="flex items-center gap-1 text-lg" aria-hidden>
          {[1, 2, 3, 4, 5].map((n) => (
            <span key={n} className={n <= (ticket.rating ?? 0) ? 'text-amber-400' : 'text-pw-border'}>
              ★
            </span>
          ))}
          <span className="ml-1 text-sm text-pw-text-muted">{ticket.rating}/5</span>
        </div>
        {ticket.ratingComment ? (
          <p className="text-sm text-pw-text-muted">“{ticket.ratingComment}”</p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className="space-y-2 rounded-xl border border-pw-border bg-pw-bg p-4"
      data-testid="vendor-ticket-rating-form"
    >
      <p className="text-sm font-medium text-pw-text">{t('rating.prompt')}</p>
      <div className="flex items-center gap-1 text-xl">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`vendor-rating-star-${n}`}
            aria-label={t('rating.starAria', { count: n })}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => setStars(n)}
            className={n <= (hover || stars) ? 'text-amber-400' : 'text-pw-border'}
          >
            ★
          </button>
        ))}
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        maxLength={2000}
        rows={2}
        placeholder={t('rating.commentPlaceholder')}
        className="w-full resize-none rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm"
      />
      {rateMut.error ? (
        <p className="text-xs text-red-600">{rateMut.error.message}</p>
      ) : null}
      <button
        type="button"
        data-testid="vendor-rating-submit"
        disabled={stars === 0 || rateMut.isPending}
        onClick={() =>
          rateMut.mutate({
            ticketId: ticket._id,
            rating: stars,
            ...(comment.trim() ? { comment: comment.trim() } : {}),
          })
        }
        className="rounded-md bg-pw-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pw-primary-strong disabled:opacity-60"
      >
        {rateMut.isPending ? t('rating.submitting') : t('rating.submit')}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mine — vendor-raised ticket detail
// ---------------------------------------------------------------------------

export function SupportMineDetailPage(): JSX.Element {
  const { t } = useTranslation('support');
  const { id = '' } = useParams<{ id: string }>();
  const detailQ = useTicketDetail(id || null);
  const replyMut = useReplyTicket();
  const myUserId = useAuth((s) => s.user?.id ?? null);
  const closeMut = useCloseTicket();
  const [replyText, setReplyText] = useState('');

  async function submitReply() {
    if (!id || !replyText.trim()) return;
    try {
      await replyMut.mutateAsync({ ticketId: id, content: replyText });
      setReplyText('');
    } catch {
      // error surfaced via mut.error
    }
  }

  const data = detailQ.data;

  return (
    <main className="flex flex-col gap-4 px-6 py-6" data-testid="support-mine-detail">
      <BackLink />
      {detailQ.isLoading ? (
        <div className="h-64 animate-pulse rounded-xl bg-pw-surface" />
      ) : !data?.ticket ? (
        <p className="py-16 text-center text-sm text-pw-text-muted">{t('thread.notFound')}</p>
      ) : (
        <>
          <PageHeader
            title={data.ticket.subject}
            titleAddon={
              <>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_PILL[displayStatus(data.ticket.status)]}`}>
                  {t(`statusLabels.${displayStatus(data.ticket.status)}`)}
                </span>
                <span className="rounded-full border border-pw-border px-2 py-0.5 text-xs text-pw-text-muted">
                  {t(`categoryLabels.${data.ticket.category}`)}
                </span>
                <span className="rounded-full border border-pw-border px-2 py-0.5 text-xs text-pw-text-muted">
                  {t(`priorityLabels.${data.ticket.priority}`)}
                </span>
              </>
            }
          />

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* Left — conversation */}
            <section className="flex flex-col rounded-xl border border-pw-border bg-pw-bg p-4 lg:col-span-2">
              <h3 className="mb-3 text-sm font-semibold text-pw-text">
                {t('detailV2.conversation', { count: data.messages.length })}
              </h3>
              <div className="flex flex-1 flex-col gap-3 overflow-y-auto">
                {data.messages.map((m) => {
                  const mine = myUserId !== null && m.authorId === myUserId;
                  return (
                    <article
                      key={m._id}
                      className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                        mine
                          ? 'self-end bg-pw-primary/10 text-pw-text'
                          : 'self-start border border-pw-border bg-pw-surface text-pw-text'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{m.content}</p>
                      <p className="mt-1 text-[10px] uppercase tracking-wide text-pw-text-muted">
                        {mine ? t('assigned.you') : t('assigned.admin')} · {fmtMs(m.createdAt)}
                      </p>
                    </article>
                  );
                })}
              </div>
              {replyMut.error ? (
                <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {replyMut.error.message}
                </p>
              ) : null}
              <footer className="mt-3 flex gap-2 border-t border-pw-border pt-3">
                <input
                  data-testid="support-ticket-message-input"
                  type="text"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={t('thread.replyPlaceholder')}
                  className="flex-1 rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm"
                  maxLength={5000}
                />
                <button
                  type="button"
                  data-testid="support-ticket-message-submit"
                  disabled={!replyText.trim() || replyMut.isPending}
                  onClick={() => void submitReply()}
                  className="rounded-md bg-pw-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pw-primary-strong disabled:opacity-60"
                >
                  {t('thread.send')}
                </button>
              </footer>
            </section>

            {/* Right — sidebar */}
            <aside className="flex flex-col gap-3">
              <div className="space-y-3 rounded-xl border border-pw-border bg-pw-bg p-4">
                <h3 className="text-sm font-semibold text-pw-text">
                  {t('detailV2.ticketInfo')}
                </h3>
                <SidebarRow label={t('listV2.colStatus')}>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_PILL[displayStatus(data.ticket.status)]}`}>
                    {t(`statusLabels.${displayStatus(data.ticket.status)}`)}
                  </span>
                </SidebarRow>
                <SidebarRow label={t('listV2.colPriority')}>
                  {t(`priorityLabels.${data.ticket.priority}`)}
                </SidebarRow>
                <SidebarRow label={t('listV2.colCategory')}>
                  {t(`categoryLabels.${data.ticket.category}`)}
                </SidebarRow>
                <SidebarRow label={t('detailV2.lastResponse')}>
                  {fmtMs(data.ticket.lastMessageAt)}
                </SidebarRow>
                {data.ticket.status !== 'Resolved' && data.ticket.status !== 'Closed' ? (
                  <div className="border-t border-pw-border pt-3">
                    <button
                      type="button"
                      data-testid="vendor-ticket-close-btn"
                      disabled={closeMut.isPending}
                      onClick={() => closeMut.mutate({ ticketId: id })}
                      className="w-full rounded-md border border-pw-border px-3 py-2 text-sm font-medium text-pw-text hover:bg-pw-surface disabled:opacity-60"
                    >
                      {closeMut.isPending ? t('close.closing') : t('close.button')}
                    </button>
                    {closeMut.error ? (
                      <p className="mt-1 text-xs text-red-600">{closeMut.error.message}</p>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {/* Rating — once the ticket reaches a terminal status the vendor
                  rates Printway support (write-once; read-only after). */}
              {data.ticket.status === 'Resolved' || data.ticket.status === 'Closed' ? (
                <RatingBlock ticket={data.ticket} />
              ) : null}
            </aside>
          </div>
        </>
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Assigned — pushed-from-admin ticket detail
// ---------------------------------------------------------------------------

export function SupportAssignedDetailPage(): JSX.Element {
  const { t } = useTranslation('support');
  const { id = '' } = useParams<{ id: string }>();
  const detailQ = useAssignedDetail(id || null);
  const replyMut = useAssignedReply();
  const statusMut = useAssignedStatusUpdate();
  const [replyText, setReplyText] = useState('');

  async function submitReply() {
    if (!id || !replyText.trim()) return;
    try {
      await replyMut.mutateAsync({ ticketId: id, body: replyText });
      setReplyText('');
    } catch {
      // error surfaced via mut.error
    }
  }

  const data = detailQ.data;

  return (
    <main className="flex flex-col gap-4 px-6 py-6" data-testid="support-assigned-detail">
      <BackLink />
      {detailQ.isLoading ? (
        <div className="h-64 animate-pulse rounded-xl bg-pw-surface" />
      ) : !data?.ticket ? (
        <p className="py-16 text-center text-sm text-pw-text-muted">{t('thread.notFound')}</p>
      ) : (
        <>
          <header>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-mono text-xl font-bold text-pw-text">
                #{data.ticket.ticket_number ?? data.ticket.id.slice(-8).toUpperCase()}
              </h1>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ASSIGNED_STATUS_PILL[data.ticket.vendor_status]}`}>
                {t(`assigned.status.${data.ticket.vendor_status}`)}
              </span>
              <span className="rounded-full border border-pw-border px-2 py-0.5 text-xs text-pw-text-muted">
                {t(`categoryLabels.${data.ticket.category}`)}
              </span>
              <span className="rounded-full border border-pw-border px-2 py-0.5 text-xs text-pw-text-muted">
                {t(`priorityLabels.${data.ticket.priority}`)}
              </span>
            </div>
            <h2 className="mt-1 text-lg font-medium text-pw-text">
              {data.ticket.subject}
            </h2>
          </header>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* Left — description + private thread */}
            <div className="flex flex-col gap-4 lg:col-span-2">
              <section className="rounded-xl border border-pw-border bg-pw-bg p-4">
                <h3 className="mb-2 text-sm font-semibold text-pw-text">
                  {t('detailV2.issueDescription')}
                </h3>
                <p className="whitespace-pre-wrap text-sm text-pw-text">
                  {data.ticket.description || '—'}
                </p>
              </section>

              <section className="flex flex-1 flex-col rounded-xl border border-pw-border bg-pw-bg p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-pw-text">
                    {t('detailV2.adminThread')}
                  </h3>
                  <span className="text-xs text-pw-text-muted">
                    {t('assigned.threadHint')}
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-3 overflow-y-auto">
                  {data.messages.length === 0 ? (
                    <p className="my-auto text-center text-xs text-pw-text-muted">
                      {t('assigned.noMessages')}
                    </p>
                  ) : (
                    data.messages.map((m) => (
                      <article
                        key={m.id}
                        className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                          m.is_vendor
                            ? 'self-end bg-pw-primary/10 text-pw-text'
                            : 'self-start border border-pw-border bg-pw-surface text-pw-text'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{m.content}</p>
                        <p className="mt-1 text-[10px] uppercase tracking-wide text-pw-text-muted">
                          {m.is_vendor ? t('assigned.you') : t('assigned.admin')} · {fmtMs(m.created_at)}
                        </p>
                      </article>
                    ))
                  )}
                </div>
                {replyMut.error ? (
                  <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {replyMut.error.message}
                  </p>
                ) : null}
                <footer className="mt-3 flex gap-2 border-t border-pw-border pt-3">
                  <input
                    data-testid="support-assigned-message-input"
                    type="text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={t('assigned.replyPlaceholder')}
                    className="flex-1 rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm"
                    maxLength={5000}
                  />
                  <button
                    type="button"
                    data-testid="support-assigned-message-submit"
                    disabled={!replyText.trim() || replyMut.isPending}
                    onClick={() => void submitReply()}
                    className="rounded-md bg-pw-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pw-primary-strong disabled:opacity-60"
                  >
                    {t('thread.send')}
                  </button>
                </footer>
              </section>
            </div>

            {/* Right — sidebar */}
            <aside className="flex flex-col gap-3">
              <div className="space-y-3 rounded-xl border border-pw-border bg-pw-bg p-4">
                <h3 className="text-sm font-semibold text-pw-text">
                  {t('assigned.workStatus')}
                </h3>
                <select
                  data-testid="support-assigned-status-select"
                  value={data.ticket.vendor_status}
                  disabled={statusMut.isPending}
                  onChange={(e) =>
                    statusMut.mutate({
                      ticketId: data.ticket.id,
                      vendorStatus: e.target.value as AssignedVendorStatus,
                    })
                  }
                  className="w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm"
                >
                  {ASSIGNED_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {t(`assigned.status.${s}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-3 rounded-xl border border-pw-border bg-pw-bg p-4">
                <h3 className="text-sm font-semibold text-pw-text">
                  {t('detailV2.ticketInfo')}
                </h3>
                <SidebarRow label={t('listV2.colCategory')}>
                  {t(`categoryLabels.${data.ticket.category}`)}
                </SidebarRow>
                <SidebarRow label={t('listV2.colPriority')}>
                  {t(`priorityLabels.${data.ticket.priority}`)}
                </SidebarRow>
                <SidebarRow label={t('listV2.colPushedAt')}>
                  {fmtMs(data.ticket.pushed_at)}
                </SidebarRow>
                {data.ticket.related_order_ids.length > 0 ? (
                  <SidebarRow label={t('detailV2.relatedOrders')}>
                    {data.ticket.related_order_ids.length}
                  </SidebarRow>
                ) : null}
              </div>
            </aside>
          </div>
        </>
      )}
    </main>
  );
}
